import React, { useState, useEffect, useContext, useRef } from 'react';
import { useStoreId, ExperienceContext } from '@thd-nucleus/experience-context';
import './bundling-product-tabs.scss';
import { useDataModel } from '@thd-nucleus/data-sources';
import { string, arrayOf, shape } from 'prop-types';
import { Carousel } from '@thd-olt-component-react/carousel';
import { BundlingTab } from './BundlingTab';
import { SelectedItemDetails } from './SelectedItemDetails';
import { dataModel } from '../dataModel';

const BundlingProductTabs = ({ itemIds = [], products = [], dataSource, packageCategory }) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('bundling-product-tabs.ready'); }, []);
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const storeId = useStoreId();
  const storeIdRef = useRef(storeId);
  const { data, loading, error, variables } = useDataModel('products', {
    variables: {
      itemIds,
      storeId
    },
    skip: !!products.length || !itemIds.length
  });

  const bundleProducts = products?.length > 0 ? products : data?.products || [];

  const initialItem = bundleProducts[0]?.itemId;

  const [selectedProductId, setSelectedProductId] = useState(itemIds[0] || initialItem || '');
  const [hoveredProductId, setHoveredProductId] = useState(itemIds[0] || initialItem || '');
  const [isTabOpen, setTabOpen] = useState(!isMobile);

  const selectedProduct = bundleProducts.find((item) => item.itemId === selectedProductId);

  if (variables?.storeId && data && !loading && !error) {
    storeIdRef.current = variables.storeId;
  }

  const handleClick = (productId) => {
    if ((!isTabOpen || productId === selectedProductId) && isMobile) {
      setTabOpen((prevState) => !prevState);
    }

    setSelectedProductId(productId);

    if (storeIdRef.current !== storeId) {
      storeIdRef.current = storeId;
    }
  };

  const handleHover = (productId) => setHoveredProductId(productId);

  const bundlingArray = bundleProducts.map((bundleProduct) => (
    <BundlingTab
      key={bundleProduct.itemId}
      itemId={bundleProduct.itemId}
      handleClick={() => handleClick(bundleProduct.itemId)}
      handleHover={() => handleHover(bundleProduct.itemId)}
      isSelected={bundleProduct.itemId === selectedProductId}
      isTabOpen={isTabOpen}
      data={bundleProduct}
    />
  ));

  const [mountFulfillment, setMountFulfillment] = useState(!isMobile);

  const loadFulfillment = (event) => {
    event.preventDefault();
    if (!mountFulfillment) {
      setMountFulfillment(true);
    }
  };

  return (
    <div className="bundling-product-tabs" data-testid="bundling-product-tabs" onMouseEnter={loadFulfillment}>
      {!isMobile ? (
        <Carousel
          itemClass="fixed-item"
          fixedItem
          multiItem={false}
          itemWidthPixelsDesktop={180}
        >
          {bundlingArray}
        </Carousel>
      )
        : (
          <div className="bundling-product-tabs__selectors">
            {bundlingArray}
          </div>
        )}
      {isTabOpen || !isMobile ? (
        <SelectedItemDetails
          storeId={storeIdRef.current}
          channel={channel}
          itemId={selectedProductId}
          hoveredItemId={hoveredProductId}
          displayMobile={isMobile}
          dataSource={dataSource}
          data={selectedProduct}
          packageCategory={packageCategory}
          mountFulfillment={mountFulfillment}
        />
      ) : null}
    </div>
  );
};

BundlingProductTabs.dataModel = dataModel;

BundlingProductTabs.displayName = 'BundlingProductTabs';

BundlingProductTabs.propTypes = {
  itemIds: arrayOf(string),
  products: arrayOf(shape),
  dataSource: string,
  packageCategory: string
};

BundlingProductTabs.defaultProps = {
  itemIds: [],
  products: [],
  dataSource: 'catalog',
  packageCategory: ''
};

export { BundlingProductTabs };
